import React from 'react';
import range from 'lodash/range';

type Props = {
  children: any,
  page: String,
  data: Number,
  currentPage: Number,
  offset?: Number,
  handleClick: Function,
};

function generatePagination(
  currentPage: Number,
  totalPages: Number,
  options: Object = { MAX_BUTTONS: 7 }
) {
  const {
    MAX_BUTTONS,
    buttonWrapperClass,
    buttonButtonClass,
    spacerWrapperClass,
    spacerButtonClass,
  } = options;
  const SKIP = (MAX_BUTTONS - 1) / 2 - 1;
  const buttons = [];
  let buttonsIndex = 0;

  const isStart = currentPage === 1;
  const isEnd = currentPage === totalPages;
  const closeToStart = currentPage - SKIP < 1;
  const closeToEnd = currentPage + SKIP > totalPages;

  let pageCounter;
  if (isStart || closeToStart) {
    pageCounter = 1;
  } else if (isEnd) {
    pageCounter = totalPages - SKIP * 2;
  } else if (closeToEnd) {
    pageCounter = totalPages - SKIP - 1;
  } else {
    pageCounter = currentPage - SKIP + 1;
  }

  let numberButtonClass;

  while (MAX_BUTTONS > buttonsIndex) {
    const isCurrentPage = currentPage === pageCounter;

    numberButtonClass = isCurrentPage
      ? 'hd-pagination__link hd-pagination__current'
      : buttonButtonClass;

    if (buttonsIndex === 0) {
      if (isStart) {
        buttons.push({
          text: '1',
          value: 1,
          liClass: spacerWrapperClass,
          buttonClass: numberButtonClass,
        });
        pageCounter += 1;
      } else {
        buttons.push({
          text: 'Prev',
          value: currentPage - 1,
          liClass: buttonWrapperClass,
          buttonClass: buttonButtonClass,
        });
      }
    } else if (buttonsIndex === 1) {
      if (!closeToStart) {
        buttons.push({
          text: '...',
          value: null,
          liClass: spacerWrapperClass,
          buttonClass: spacerButtonClass,
        });
      } else {
        buttons.push({
          text: pageCounter,
          value: pageCounter,
          liClass: spacerWrapperClass,
          buttonClass: numberButtonClass,
        });
        pageCounter += 1;
      }
    } else if (buttonsIndex === MAX_BUTTONS - 2) {
      if (!closeToEnd) {
        buttons.push({
          text: '...',
          value: null,
          liClass: spacerWrapperClass,
          buttonClass: spacerButtonClass,
        });
      } else {
        buttons.push({
          text: pageCounter,
          value: pageCounter,
          liClass: spacerWrapperClass,
          buttonClass: numberButtonClass,
        });
        pageCounter += 1;
      }
    } else if (buttonsIndex === MAX_BUTTONS - 1) {
      if (isEnd) {
        buttons.push({
          text: totalPages,
          value: totalPages,
          liClass: spacerWrapperClass,
          buttonClass: numberButtonClass,
        });
      } else {
        buttons.push({
          text: 'Next',
          value: currentPage + 1,
          liClass: buttonWrapperClass,
          buttonClass: buttonButtonClass,
        });
        pageCounter += 1;
      }
    } else {
      buttons.push({
        text: pageCounter,
        value: pageCounter,
        liClass: spacerWrapperClass,
        buttonClass: numberButtonClass,
      });

      pageCounter += 1;
    }
    buttonsIndex += 1;
  }
  return buttons;
}

export function createPagination(currentPage: Number, totalPages: Number) {
  const options = {
    MAX_BUTTONS: 7,
    buttonWrapperClass: 'hd-pagination__item hd-pagination__button',
    buttonButtonClass: 'hd-pagination__link',
    currentButtonClass: 'hd-pagination__link hd-pagination__current',
    spacerWrapperClass: 'hd-pagination__item',
    spacerButtonClass: 'hd-pagination__spacer',
  };

  if (totalPages <= options.MAX_BUTTONS) {
    return range(1, totalPages + 1).map((val: Number) => {
      const buttonClass =
        val === +currentPage
          ? options.currentButtonClass
          : options.buttonButtonClass;
      return {
        text: val,
        value: val,
        liClass: options.spacerWrapperClass,
        buttonClass,
      };
    });
  }
  return generatePagination(+currentPage, totalPages, options);
}

function Pagination(props: Props) {
  const { children, handleClick, data = [], currentPage, offset } = props;

  const totalPages = Math.ceil(data.length / offset);
  if (totalPages < 2) {
    // https://reactjs.org/docs/jsx-in-depth.html#children-in-jsx
    return children({ data });
  }

  const startIndex = (currentPage - 1) * offset;
  const endIndex = currentPage * offset;
  const currentData = data.slice(startIndex, endIndex);

  const paginationButtons = createPagination(currentPage, totalPages);

  return (
    <>
      {/* https://reactjs.org/docs/jsx-in-depth.html#children-in-jsx */}
      {children({ data: currentData })}
      {
        <div className="col__12-12">
          <nav
            className="hd-pagination grid"
            style={{ justifyContent: 'center' }}
            type="navigation"
          >
            <ul
              data-automation-id="authOrderPagination"
              className="hd-pagination__wrapper"
              style={{ margin: '1em' }}
            >
              {paginationButtons.map(
                (
                  { text, value, liClass, buttonClass }: Object,
                  index: Number
                ) => (
                  <li
                    data-automation-id={`authOrderPagination-${text}-${value}`}
                    key={`${index + 1}`}
                    className={liClass}
                  >
                    <button
                      onClick={handleClick}
                      value={value}
                      className={buttonClass}
                      type="button"
                    >
                      {text}
                    </button>
                  </li>
                )
              )}
            </ul>
          </nav>
        </div>
      }
    </>
  );
}

Pagination.defaultProps = {
  offset: 10,
};

export default Pagination;
